<template>
  <div class="popup">
    <div class="popup-header">
      <MapPin class="popup-icon" />
      <div>
        <b>{{ dataset.name }}</b>
        <div v-if="hospitalData && hospitalData.status" class="greyed-out">
          {{ hospitalData.status.source }}
        </div>
      </div>
    </div>
    <div class="popup-body">
      <div v-if="isLoading" class="centered"><Spinner /></div>
      <template v-else>
        <div v-if="hospitalData && hospitalData.status" class="hospital-status">
          <div>Reported Status</div>
          <div class="timestamp greyed-out">{{ timestamp }}</div>
          <div v-for="item in statuses" :key="item.label" class="resource">
            <StatusDot :fillColor="item.color" />
            <div class="resource-status">
              <div>{{ item.label }}</div>
              <div class="greyed-out availability">{{ item.status }}</div>
            </div>
          </div>
        </div>
        <div v-else>No data available.</div>
      </template>
    </div>
    <BaseButton variant="primary" tag="RouterLink" :to="reporterRoute">
      Report status
    </BaseButton>
  </div>
</template>

<script>
import BaseButton from '@/components/base-button';
import MapPin from '@/assets/map-pin.svg';
import Spinner from '@/components/static-donut-chart';
import StatusDot from '@/components/status-dot';
import formatDistance from 'date-fns/formatDistance';
import get from 'lodash/get';
import {
  AggregateChartColors,
  CapacityCategoryLabels,
  HospitalResources,
} from '@/store/types';
import { RouterAliases } from '@/router';
import { StoreActions, StoreGetters } from '@/store';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: { BaseButton, MapPin, Spinner, StatusDot },
  colors: AggregateChartColors,
  props: {
    dataset: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      getHospitalData: StoreGetters.GET_HOSPITAL_DETAILS_BY_ID,
    }),
    hospitalData() {
      return this.getHospitalData(this.dataset.id);
    },
    reporterRoute() {
      return {
        name: RouterAliases.HOSPITAL_REPORT,
        params: {
          hospitalId: this.dataset.id,
          step: 1,
        },
      };
    },
    statuses() {
      const getReadableStatus = status =>
        CapacityCategoryLabels[status.toUpperCase()] || 'No data';
      return Object.keys(HospitalResources).map(item => {
        const key = item.toLowerCase();
        const [status] = Object.entries(this.dataset[key]).find(
          ([, value]) => value,
        );
        return {
          color: AggregateChartColors[status.toUpperCase()],
          label: `${HospitalResources[item]}s`,
          status: getReadableStatus(status),
        };
      });
    },
    timestamp() {
      const updatedAt = get(this.hospitalData, 'updated_at', null);
      return updatedAt ? `${formatDistance(updatedAt)} ago` : '';
    },
  },
  watch: {
    isOpen: {
      async handler(newVal) {
        // Only load hospital data when the popup is opened and it hasn't
        // already been loaded.
        if (newVal && !this.hospitalData) {
          await this.loadHospitalDetails(this.dataset.id);
          this.isLoading = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      loadHospitalDetails: StoreActions.LOAD_HOSPITAL_DETAILS,
    }),
  },
};
</script>

<style>
.mapboxgl-popup {
  max-width: 300px !important;
  z-index: 1;
}

.mapboxgl-popup-content {
  border-radius: 6px;
  box-shadow: 0px 0px 25px rgba(27, 7, 83, 0.1);
  font-family: 'ttcommons';
  font-weight: 500;
  padding: 20px;
}
</style>

<style scoped>
.popup {
  font-size: 13px;
}

.popup-header {
  align-items: flex-start;
  border-bottom: 1px solid var(--off-white);
  display: flex;
  font-size: 15px;
  padding-bottom: 8px;
}

.popup-icon {
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 4px;
}

.greyed-out {
  color: #b7b7b7;
}

.popup-body {
  margin: 16px 0 24px;
}

.centered {
  display: flex;
  justify-content: center;
}

.btn-icon {
  flex-shrink: 0;
  margin-left: 24px;
}

.hospital-status {
  display: grid;
  grid-column-gap: 8px;
  grid-row-gap: 4px;
  grid-template-columns: 1fr 1fr;
}

.resource {
  display: flex;
}

.status-dot {
  flex-shrink: 0;
  margin-right: 8px;
}

.reported-status {
  flex-grow: 1;
}

.availability {
  font-size: 11px;
}
</style>
